import { useState, useMemo, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import CircularProgress from "@mui/material/CircularProgress";

const EMPTY = {
  username: "",
  name: "",
  first_surname: "",
  second_surname: "",
  email: "",
  password: "",
  password2: "",
  groups: [],
  is_admin: false,
  disabled: false,
};

function normalizeGroupIds(values = []) {
  if (!Array.isArray(values)) return [];

  return values
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
}

function getUserGroups(user) {
  if (!user) return [];

  if (Array.isArray(user.groups)) {
    return user.groups
      .map((g) =>
        typeof g === "object" && g !== null
          ? g.id ?? g._id
          : g
      )
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String);
  }

  // compatibilidad temporal con registros/respuestas anteriores
  if (
    user.group_id !== null &&
    user.group_id !== undefined &&
    user.group_id !== ""
  ) {
    return [String(user.group_id)];
  }

  return [];
}

export default function FormUser({
  form,
  setForm,
  user,
  mode = "edit",
  loading = false,
  groups = [],
  saving = false,
  errors = [],
  onSave,
  onCancel,
}) {
  const gruposCargando = !Array.isArray(groups);
  const gruposVacios = Array.isArray(groups) && groups.length === 0;

  const [localForm, setLocalForm] = useState(EMPTY);
  const f = form ?? localForm;
  const setF = setForm ?? setLocalForm;

  // snapshot del usuario original
  const original = useMemo(
    () =>
      user
        ? {
            username: user.username ?? "",
            name: user.name ?? "",
            first_surname: user.first_surname ?? "",
            second_surname: user.second_surname ?? "",
            email: user.email ?? "",
            groups: getUserGroups(user),
            is_admin: !!user.is_admin,
            disabled: !!user.disabled,
          }
        : EMPTY,
    [user]
  );

  // hidratar formulario cuando llega el usuario
  useEffect(() => {
    if (!user) return;
    setF({
      username: user.username ?? "",
      name: user.name ?? "",
      first_surname: user.first_surname ?? "",
      second_surname: user.second_surname ?? "",
      email: user.email ?? "",
      groups: getUserGroups(user),
      is_admin: !!user.is_admin,
      disabled: !!user.disabled,
      password: "",
      password2: "",
    });
  }, [user, setF]);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const setChk = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.checked }));

  const validEmail = useMemo(() => /\S+@\S+\.\S+/.test(f.email || ""), [f.email]);

  const passMatch = useMemo(
    () => !f.password || f.password === f.password2,
    [f.password, f.password2]
  );

  // Detectar si hubo cambios reales
  const hasChanges = useMemo(() => {
    const keys = [
      "username",
      "name",
      "first_surname",
      "second_surname",
      "email",
      "is_admin",
      "disabled",
    ];

    for (const k of keys) {
      if (String(f[k] ?? "") !== String(original[k] ?? "")) {
        return true;
      }
    }

    const currentGroups = [...(f.groups || [])]
      .map(String)
      .sort();

    const originalGroups = [...(original.groups || [])]
      .map(String)
      .sort();

    if (
      currentGroups.length !== originalGroups.length ||
      currentGroups.some((id, index) => id !== originalGroups[index])
    ) {
      return true;
    }

    if (f.password?.trim()) return true;

    return false;
  }, [f, original]);

  const canSave =
    validEmail && passMatch && hasChanges && !saving;

  const handleSave = () => {
    if (!onSave) return;

    if (!f.is_admin && (!Array.isArray(f.groups) || f.groups.length === 0)) {
      return;
    }

    const payload = {
      email: f.email?.trim(),
      username: f.username?.trim() || undefined,
      name: f.name?.trim() || undefined,
      first_surname: f.first_surname?.trim() || undefined,
      second_surname: f.second_surname?.trim() || undefined,
      groups: f.is_admin
        ? []
        : normalizeGroupIds(f.groups),
      is_admin: !!f.is_admin,
      disabled: !!f.disabled,
    };

    if (f.password) payload.password = f.password;

    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === undefined) delete payload[k];
    });

    onSave(payload);
  };

  return (
    <Card sx={{ p: 3 }}>
      <SoftTypography variant="h6" fontWeight="medium" gutterBottom>
        Editar usuario
      </SoftTypography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((m, i) => (
            <div key={i}>{m}</div>
          ))}
        </Alert>
      )}

      {loading ? (
        <SoftBox display="flex" alignItems="center" gap={1} py={2}>
          <CircularProgress size={18} /> <SoftTypography>Cargando…</SoftTypography>
        </SoftBox>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nombre" value={f.name} onChange={set("name")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Usuario (username)"
                value={f.username}
                onChange={set("username")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primer apellido"
                value={f.first_surname}
                onChange={set("first_surname")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Segundo apellido"
                value={f.second_surname}
                onChange={set("second_surname")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={f.email}
                onChange={set("email")}
                error={!!f.email && !validEmail}
                helperText={
                  !!f.email && !validEmail ? "Email no válido" : " "
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contraseña (opcional)"
                type="password"
                value={f.password}
                onChange={set("password")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Repite contraseña"
                type="password"
                value={f.password2}
                onChange={set("password2")}
                error={!!f.password && !passMatch}
                helperText={
                  !!f.password && !passMatch ? "Las contraseñas no coinciden" : " "
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                limitTags={3}
                options={Array.isArray(groups) ? groups : []}
                value={(Array.isArray(groups) ? groups : []).filter((g) =>
                  (f.groups || [])
                    .map(String)
                    .includes(String(g.id ?? g._id))
                )}
                onChange={(_event, selectedGroups) => {
                  setF((prev) => ({
                    ...prev,
                    groups: selectedGroups.map((g) =>
                      String(g.id ?? g._id)
                    ),
                  }));
                }}
                getOptionLabel={(g) =>
                  g?.name ??
                  g?.nombre ??
                  `Grupo ${g?.id ?? g?._id ?? ""}`
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id ?? option._id) ===
                  String(value.id ?? value._id)
                }
                disabled={gruposCargando || f.is_admin}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    minHeight: 56,
                    alignItems: "center",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grupos"
                    required={!f.is_admin}
                    placeholder={
                      !f.groups?.length
                        ? "Selecciona uno o varios grupos"
                        : ""
                    }
                    helperText={
                      f.is_admin
                        ? "Los administradores tienen acceso a todos los grupos."
                        : gruposVacios
                        ? "No hay grupos disponibles."
                        : "Puedes seleccionar uno o varios grupos."
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <SoftBox
                display="flex"
                gap={4}
                alignItems="center"
                flexWrap="wrap"
                mt={1}
              >
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={f.is_admin}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setF((prev) => ({
                        ...prev,
                        is_admin: checked,
                        groups: checked ? [] : prev.groups,
                      }));
                    }}
                  />

                  <SoftTypography>
                    Administrador
                  </SoftTypography>
                </SoftBox>

                <SoftBox display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={f.disabled}
                    onChange={setChk("disabled")}
                  />

                  <SoftTypography>
                    {f.disabled ? "Suspendido" : "Activo"}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Grid>
          </Grid>

          <SoftBox mt={3} display="flex" gap={1}>
            <SoftButton color="dark" variant="gradient" onClick={handleSave} disabled={!canSave}>
              {saving ? "Guardando..." : "Guardar"}
            </SoftButton>
            <SoftButton color="secondary" variant="outlined" onClick={onCancel}>
              Cancelar
            </SoftButton>
          </SoftBox>
        </>
      )}
    </Card>
  );
}
